import {observe} from '@github-ui/selector-observer'
import {on} from 'delegated-events'
import {reportError} from '@github-ui/failbot'
import {
  applyCopilotReviewEffortResponse,
  captureCopilotReviewEffort,
  copilotReviewEffortFieldNames,
  copilotReviewEffortPickerSelector,
  enhanceCopilotReviewEffortPicker,
  getCopilotReviewEffortContext,
  isCurrentCopilotReviewEffort,
  isRelatedCopilotReviewEffort,
  InvalidCopilotReviewEffortError,
  preserveCopilotReviewEffortState,
  readCopilotReviewEffortInput,
  readCopilotReviewEffortResponse,
  recordCopilotReviewEffortSelection,
  type CopilotReviewEffortSubmission,
} from '@github-ui/pull-request-page-data-tooling/copilot-review-effort'

const effortSubmissions = new WeakMap<HTMLFormElement, CopilotReviewEffortSubmission>()
const reportedCaptureFailures = new WeakSet<HTMLFormElement>()
const sidebarEffortPickers = new WeakMap<Element, Map<string, Element>>()
const pickerLocations = new WeakMap<Element, {scope: Element; context: string}>()

type SidebarRequestState = {submitted: number; applied: number; form: HTMLFormElement}
type CopilotReviewEffortSidebarSubmission = {
  effort: CopilotReviewEffortSubmission | undefined
  context: string
  requestNumber: number
  requestState: SidebarRequestState
}

const sidebarRequests = new WeakMap<CopilotReviewEffortSubmission['state'] | HTMLFormElement, SidebarRequestState>()

function recordCopilotReviewEffortSubmission(event: FormDataEvent) {
  const form = event.currentTarget as HTMLFormElement
  let submission
  try {
    submission = captureCopilotReviewEffort(form)
    if (
      !submission &&
      form.querySelector(
        '[data-copilot-review-effort-version], [data-copilot-review-effort-initial-state], input[name="review_effort_client_version"], input[name="review_effort_level_selected"], input[name="review_effort_source"]',
      ) &&
      (form.querySelector('[data-copilot-review-effort-picker]') || !readCopilotReviewEffortResponse(form))
    ) {
      throw new InvalidCopilotReviewEffortError()
    }
  } catch (error) {
    if (!reportedCaptureFailures.has(form)) {
      reportedCaptureFailures.add(form)
      reportError(error)
    }
    for (const name of copilotReviewEffortFieldNames) event.formData.delete(name)
    effortSubmissions.delete(form)
    return
  }
  if (submission) {
    for (const name of copilotReviewEffortFieldNames) event.formData.delete(name)
    for (const [name, value] of Object.entries(submission.fields)) event.formData.set(name, String(value))
    effortSubmissions.set(form, submission)
  } else {
    effortSubmissions.delete(form)
  }
}

on('formdata', 'form', recordCopilotReviewEffortSubmission, {capture: true})

export function takeCopilotReviewEffortSubmission(form: HTMLFormElement, body: RequestInit['body']) {
  const submission = body instanceof FormData ? effortSubmissions.get(form) : undefined
  effortSubmissions.delete(form)
  if (
    !(body instanceof FormData) ||
    (!submission && !sidebarRequests.has(form) && !form.querySelector('[data-copilot-review-effort-picker]'))
  ) {
    return
  }

  const requestState = sidebarRequests.get(submission?.state ?? form) ??
    sidebarRequests.get(form) ?? {submitted: 0, applied: 0, form}
  requestState.submitted += 1
  requestState.form = form
  sidebarRequests.set(form, requestState)
  if (submission) sidebarRequests.set(submission.state, requestState)
  return {
    effort: submission,
    context: submission?.context ?? getCopilotReviewEffortContext(form),
    requestNumber: requestState.submitted,
    requestState,
  }
}

type SidebarReconciliationResult =
  | {status: 'replace'; form: HTMLFormElement; replacement: DocumentFragment}
  | {status: 'ignored'}

function getRelatedCopilotReviewEffortForm(
  form: HTMLFormElement,
  sidebarSubmission: CopilotReviewEffortSidebarSubmission | undefined,
) {
  const submission = sidebarSubmission?.effort
  if (submission && !isRelatedCopilotReviewEffort(submission)) return
  const currentForm = submission?.picker.closest('form') ?? sidebarSubmission?.requestState.form ?? form
  if (
    currentForm.isConnected &&
    (!sidebarSubmission || getCopilotReviewEffortContext(currentForm) === sidebarSubmission.context)
  ) {
    return currentForm
  }
}

function isLatestCopilotReviewEffortSidebarSubmission(submission: CopilotReviewEffortSidebarSubmission | undefined) {
  return !submission || submission.requestNumber === submission.requestState.submitted
}

export function shouldShowCopilotReviewEffortError(
  form: HTMLFormElement,
  sidebarSubmission: CopilotReviewEffortSidebarSubmission | undefined,
) {
  return (
    getRelatedCopilotReviewEffortForm(form, sidebarSubmission) !== undefined &&
    isLatestCopilotReviewEffortSidebarSubmission(sidebarSubmission)
  )
}

export function reconcileCopilotReviewEffortResponse(
  form: HTMLFormElement,
  sidebarSubmission: CopilotReviewEffortSidebarSubmission | undefined,
  response: {html: DocumentFragment},
): SidebarReconciliationResult {
  const submission = sidebarSubmission?.effort
  const currentForm = getRelatedCopilotReviewEffortForm(form, sidebarSubmission)
  if (!currentForm) return {status: 'ignored'}
  const replaceSidebar = !sidebarSubmission || sidebarSubmission.requestNumber > sidebarSubmission.requestState.applied
  if (!replaceSidebar && submission && !isCurrentCopilotReviewEffort(submission)) return {status: 'ignored'}

  const replacement = response.html
  if (submission) {
    try {
      if (!reconcileSubmittedPicker(submission, replacement, replaceSidebar)) {
        reportError(new InvalidCopilotReviewEffortError())
      }
    } catch (error) {
      reportError(error)
    }
  }
  if (!replaceSidebar) return {status: 'ignored'}
  if (sidebarSubmission) {
    sidebarSubmission.requestState.applied = sidebarSubmission.requestNumber
    const nextForms = Array.from(replacement.querySelectorAll('form')).filter(
      next => getCopilotReviewEffortContext(next) === sidebarSubmission.context,
    )
    if (nextForms.length === 1 && nextForms[0]) {
      sidebarSubmission.requestState.form = nextForms[0]
      sidebarRequests.set(nextForms[0], sidebarSubmission.requestState)
    }
  }
  return {status: 'replace', form: currentForm, replacement}
}

function reconcileSubmittedPicker(
  submission: CopilotReviewEffortSubmission,
  replacement: DocumentFragment,
  replaceSidebar: boolean,
) {
  const acknowledgments = replacement.querySelectorAll('[data-copilot-review-effort-response]')
  const acknowledgment =
    acknowledgments.length === 1 && acknowledgments[0]
      ? readCopilotReviewEffortResponse(acknowledgments[0], 'data-copilot-review-effort-response')
      : undefined
  const accepted = acknowledgment && !acknowledgment.review_effort_level_selected ? acknowledgment : undefined
  if (acknowledgments.length > 0 && !accepted) reportError(new InvalidCopilotReviewEffortError())
  if (!replaceSidebar) {
    if (accepted) applyCopilotReviewEffortResponse(submission, accepted)
    return true
  }
  const candidates = Array.from(replacement.querySelectorAll(copilotReviewEffortPickerSelector))
  const previousPicker = submission.picker
  if (candidates.length === 0) {
    const legacyPicker = findLegacyCopilotReviewEffortPicker(previousPicker, replacement)
    if (legacyPicker === 'incomplete') return false
    if (legacyPicker) {
      if (!readCopilotReviewEffortInput(previousPicker)) return false
      if (accepted) applyCopilotReviewEffortResponse(submission, accepted)
      if (preserveCopilotReviewEffortState(previousPicker, legacyPicker) !== 'preserved') return false
      unregisterCopilotReviewEffortPicker(previousPicker)
      return true
    }
    const containers = findCopilotReviewEffortContainers(replacement, submission.context)
    const [container] = containers
    if (
      !container ||
      containers.length !== 1 ||
      copilotReviewEffortFieldNames.some(name => container.querySelector(`input[name="${name}"]`))
    ) {
      return false
    }
    if (accepted) applyCopilotReviewEffortResponse(submission, accepted)
    if (!preserveCopilotReviewEffortInHiddenInputs(previousPicker, container, submission.context)) return false
    unregisterCopilotReviewEffortPicker(previousPicker)
    return true
  }

  const matching = candidates.filter(candidate => getCopilotReviewEffortContext(candidate) === submission.context)
  const [replacementPicker] = matching
  if (!replacementPicker || matching.length !== 1) return false
  if (!readCopilotReviewEffortInput(replacementPicker) || !readCopilotReviewEffortInput(previousPicker)) return false
  if (accepted) applyCopilotReviewEffortResponse(submission, accepted)

  if (preserveCopilotReviewEffortState(previousPicker, replacementPicker) === 'incomplete') {
    return false
  }
  unregisterCopilotReviewEffortPicker(previousPicker)
  enhanceCopilotReviewEffortPicker(replacementPicker)
  return true
}

export function preserveCopilotReviewEffortInPreview(picker: Element | null, replacement: DocumentFragment) {
  const candidates = replacement.querySelectorAll(copilotReviewEffortPickerSelector)
  if (candidates.length > 1) return 'incomplete'
  const replacementPicker =
    candidates[0] ?? (picker ? findLegacyCopilotReviewEffortPicker(picker, replacement) : undefined)
  if (replacementPicker === 'incomplete') return 'incomplete'
  if (
    picker &&
    replacementPicker &&
    getCopilotReviewEffortContext(picker) === getCopilotReviewEffortContext(replacementPicker, picker.closest('form'))
  ) {
    return preserveCopilotReviewEffortState(picker, replacementPicker)
  }
}

function findLegacyCopilotReviewEffortPicker(
  previous: Element,
  replacement: DocumentFragment,
): Element | 'incomplete' | undefined {
  const context = getCopilotReviewEffortContext(previous)
  const inputs = replacement.querySelectorAll<HTMLInputElement>('input[name="review_effort_level"]')
  if (inputs.length === 0) return
  const input = inputs[0]
  const containers = findCopilotReviewEffortContainers(replacement, context, previous.closest('form'))
  if (
    inputs.length !== 1 ||
    !input ||
    input.type !== 'hidden' ||
    containers.length !== 1 ||
    input.closest('.js-issue-sidebar-form') !== containers[0] ||
    !input.closest('.js-discussion-sidebar-item') ||
    replacement.querySelector('[data-copilot-review-effort-version], [data-copilot-review-effort-initial-state]') ||
    copilotReviewEffortFieldNames.some(
      name => name !== 'review_effort_level' && replacement.querySelector(`input[name="${name}"]`),
    )
  ) {
    return 'incomplete'
  }

  const menu = input.closest('action-menu')
  let picker = menu?.parentElement
  if (menu) {
    if (
      !menu.matches('[data-select-variant="single"]') ||
      !picker ||
      picker.matches('.js-issue-sidebar-form') ||
      picker.querySelectorAll('action-menu').length !== 1
    ) {
      return 'incomplete'
    }
  } else {
    picker = input.ownerDocument.createElement('div')
    picker.hidden = true
    input.replaceWith(picker)
    picker.append(input)
  }
  if (!picker) return 'incomplete'
  picker.setAttribute('data-copilot-review-effort-picker', '')
  return picker
}

function findCopilotReviewEffortContainers(scope: ParentNode, context: string, fallbackForm?: HTMLFormElement | null) {
  const containers = Array.from(scope.querySelectorAll('.js-issue-sidebar-form'))
  if (scope instanceof Element && scope.matches('.js-issue-sidebar-form')) containers.unshift(scope)
  return containers.filter(container => {
    const form = container instanceof HTMLFormElement ? container : (container.closest('form') ?? fallbackForm)
    return form && getCopilotReviewEffortContext(container, form) === context
  })
}

function preserveCopilotReviewEffortInHiddenInputs(picker: Element, container: Element, context: string) {
  if (copilotReviewEffortFieldNames.some(name => container.querySelector(`input[name="${name}"]`))) return

  const hiddenPicker = picker.ownerDocument.createElement('div')
  hiddenPicker.hidden = true
  hiddenPicker.setAttribute('data-copilot-review-effort-picker', '')
  hiddenPicker.setAttribute('data-copilot-review-effort-version', '2')
  container.append(hiddenPicker)
  if (getCopilotReviewEffortContext(hiddenPicker) !== context) {
    hiddenPicker.remove()
    return
  }
  const result = preserveCopilotReviewEffortState(picker, hiddenPicker)
  if (result !== 'preserved') {
    hiddenPicker.remove()
    if (result === 'incomplete') reportError(new InvalidCopilotReviewEffortError())
    return
  }
  return hiddenPicker
}

function registerCopilotReviewEffortPicker(picker: Element, location: {scope: Element; context: string}) {
  let pickers = sidebarEffortPickers.get(location.scope)
  if (!pickers) {
    pickers = new Map()
    sidebarEffortPickers.set(location.scope, pickers)
  }
  pickers.set(location.context, picker)
  pickerLocations.set(picker, location)
}

function unregisterCopilotReviewEffortPicker(picker: Element) {
  const location = pickerLocations.get(picker)
  if (!location) return
  const pickers = sidebarEffortPickers.get(location.scope)
  if (pickers?.get(location.context) === picker) pickers.delete(location.context)
  pickerLocations.delete(picker)
}

observe(copilotReviewEffortPickerSelector, {
  add(picker) {
    try {
      enhanceCopilotReviewEffortPicker(picker)
    } catch (error) {
      reportError(error)
      return
    }
    const scope = picker.closest('.js-discussion-sidebar-item')?.parentElement
    if (!scope) return

    const context = getCopilotReviewEffortContext(picker)
    const previous = sidebarEffortPickers.get(scope)?.get(context)
    if (previous && previous !== picker) {
      if (previous.isConnected) return
      if (
        readCopilotReviewEffortInput(previous) &&
        preserveCopilotReviewEffortState(previous, picker, true) === 'incomplete'
      ) {
        reportError(new InvalidCopilotReviewEffortError())
      }
      unregisterCopilotReviewEffortPicker(previous)
    }
    registerCopilotReviewEffortPicker(picker, {scope, context})
  },
  remove(picker) {
    if (picker.isConnected) return
    const location = pickerLocations.get(picker)
    if (!location) return
    if (!readCopilotReviewEffortInput(picker)) {
      unregisterCopilotReviewEffortPicker(picker)
      return
    }
    const replacement = Array.from(location.scope.querySelectorAll(copilotReviewEffortPickerSelector)).find(
      candidate => candidate !== picker && getCopilotReviewEffortContext(candidate) === location.context,
    )
    if (replacement) {
      if (preserveCopilotReviewEffortState(picker, replacement, true) === 'incomplete') {
        reportError(new InvalidCopilotReviewEffortError())
      }
      unregisterCopilotReviewEffortPicker(picker)
      registerCopilotReviewEffortPicker(replacement, location)
      return
    }
    const containers = findCopilotReviewEffortContainers(location.scope, location.context).filter(
      container => container.isConnected,
    )
    const [container] = containers
    if (container && containers.length === 1) {
      preserveCopilotReviewEffortInHiddenInputs(picker, container, location.context)
    }
    unregisterCopilotReviewEffortPicker(picker)
  },
})

function markCopilotReviewEffortExplicitlySelected(event: CustomEvent<{item: HTMLElement}>) {
  const picker = event.currentTarget as HTMLElement
  try {
    recordCopilotReviewEffortSelection(picker, event.detail.item)
    if (readCopilotReviewEffortInput(picker)) {
      const scope = picker.closest('.js-discussion-sidebar-item')?.parentElement
      if (scope) registerCopilotReviewEffortPicker(picker, {scope, context: getCopilotReviewEffortContext(picker)})
    }
  } catch (error) {
    reportError(error)
  }
}

on('itemActivated', copilotReviewEffortPickerSelector, markCopilotReviewEffortExplicitlySelected)
